import { useEffect, useRef, useState, type ReactNode } from 'react';
import { ChevronRight } from 'lucide-react';
import { cn } from './cn';

/**
 * Ant Design `Tree` replacement — expand/collapse hierarchy with `treeData`
 * ({ key, title, children }). Covers the read-only display use in-app
 * (defaultExpandAll, showLine, blockNode, selectable). Optional onSelect.
 */
export interface TreeNode {
  key: string | number;
  title?: ReactNode;
  children?: TreeNode[];
  [k: string]: any;
}

export interface TreeProps {
  treeData?: TreeNode[];
  defaultExpandAll?: boolean;
  /** Expand nodes down to this depth on first data load (roots = depth 0, so 1 opens the root
   *  rows and leaves their children visible-but-collapsed). Ignored when defaultExpandAll is set. */
  defaultExpandDepth?: number;
  showLine?: boolean | { showLeafIcon?: boolean };
  blockNode?: boolean;
  selectable?: boolean;
  className?: string;
  fieldNames?: { title?: string; key?: string; children?: string };
  onSelect?: (keys: (string | number)[], info: { node: TreeNode }) => void;
}

function collectKeys(nodes: TreeNode[], kf: string, cf: string, acc: Set<string>, depth = 0, maxDepth = Infinity) {
  for (const n of nodes) {
    const ch = n[cf] as TreeNode[] | undefined;
    if (ch?.length && depth < maxDepth) { acc.add(String(n[kf])); collectKeys(ch, kf, cf, acc, depth + 1, maxDepth); }
  }
}

export function Tree({
  treeData = [], defaultExpandAll, defaultExpandDepth, showLine, blockNode, selectable = true, className, fieldNames, onSelect,
}: TreeProps) {
  const kf = fieldNames?.key ?? 'key';
  const tf = fieldNames?.title ?? 'title';
  const cf = fieldNames?.children ?? 'children';
  const line = !!showLine;

  // Seed the initial expanded set from the default* props. treeData is usually fetched (async), so
  // the seed must also run once when the data first lands — a plain useState initializer runs on the
  // empty first render and would expand nothing (the bug that left the territory tree collapsed).
  const wantsDefault = defaultExpandAll || (defaultExpandDepth != null && defaultExpandDepth > 0);
  const seedExpanded = () => {
    const s = new Set<string>();
    if (defaultExpandAll) collectKeys(treeData, kf, cf, s);
    else if (defaultExpandDepth != null && defaultExpandDepth > 0) collectKeys(treeData, kf, cf, s, 0, defaultExpandDepth);
    return s;
  };
  const seededRef = useRef(false);
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const s = seedExpanded();
    if (treeData.length) seededRef.current = true; // real data present at mount → already seeded
    return s;
  });
  // Re-seed exactly once, when async data first arrives (mount had none). The ref guard stops a later
  // refetch from stomping the user's manual expand/collapse.
  useEffect(() => {
    if (seededRef.current || !treeData.length) return;
    if (wantsDefault) setExpanded(seedExpanded());
    seededRef.current = true;
  }, [treeData]);
  const toggle = (k: string) => setExpanded((prev) => {
    const s = new Set(prev);
    if (s.has(k)) s.delete(k); else s.add(k);
    return s;
  });

  const renderNodes = (nodes: TreeNode[], depth: number): ReactNode => (
    <ul className={cn('m-0 list-none p-0', depth > 0 && line && 'ml-[11px] border-l border-line pl-3', depth > 0 && !line && 'ml-5')}>
      {nodes.map((n) => {
        const key = String(n[kf]);
        const kids = n[cf] as TreeNode[] | undefined;
        const hasKids = !!kids?.length;
        const isOpen = expanded.has(key);
        return (
          <li key={key} className="py-0.5">
            <div
              className={cn('flex items-center gap-1 rounded-control px-1.5 py-1 text-sm text-content', blockNode && 'w-full', selectable && 'cursor-pointer hover:bg-surface-2')}
              onClick={() => { if (selectable) onSelect?.([n[kf]], { node: n }); }}
            >
              {hasKids ? (
                <button
                  type="button"
                  aria-label={isOpen ? 'Collapse' : 'Expand'}
                  onClick={(e) => { e.stopPropagation(); toggle(key); }}
                  className="grid size-4 shrink-0 place-items-center text-subtle hover:text-content"
                >
                  <ChevronRight className={cn('size-3.5 transition-transform', isOpen && 'rotate-90')} />
                </button>
              ) : (
                <span className="inline-block size-4 shrink-0" />
              )}
              <span className="min-w-0 flex-1 truncate">{n[tf]}</span>
            </div>
            {hasKids && isOpen && renderNodes(kids!, depth + 1)}
          </li>
        );
      })}
    </ul>
  );

  return <div className={cn('gb-tree', className)}>{renderNodes(treeData, 0)}</div>;
}
